import {
  ErrorBoundary,
  Loading,
  LookingForSomethingElse,
  publishEvent,
  SectionHeader,
  useRefetch,
  validateObjectProperties
} from '@uma/react-commons/dist/index';
import { Button, IconReload } from '@veneer/core';
import React, { useEffect, useMemo, useState } from 'react';
import { DeviceFeatureFlagProps } from 'src/App';
import DeviceCard from 'src/components/DeviceCard';
import { DoNotSeeDevice } from 'src/components/DoNotSeeDevice';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import GET_DEVICES from 'src/graphql/getDevices';
import { ContainerContent, Section } from 'src/styles/styles';
import { Device } from 'src/types/IDevices';
import { screenPath } from 'src/utils/constants';
import { DeviceType, DeviceTypeParam, ScreenName } from 'src/utils/enums';
import {
  DevicesError,
  DevicesScreenDisplayed,
  RefreshDevicesButtonClicked
} from './analytics';
import {
  DevicesContainer,
  DevicesList,
  Header,
  IconGhost,
  NoDevices,
  NoDevicesSection,
  NoDevicesSectionContent,
  RefreshSection
} from './styles';

const Devices = ({
  isLoading,
  originalFeedbackExperience,
  seeDevicesPurchasedOnlyForUS,
  showControlHubLabel
}: DeviceFeatureFlagProps) => {
  const operation = 'GET_DEVICES';
  const { translate, northboundAPIs } = useDependencyManagerContext();
  const [refetchLoading, setRefetchLoading] = useState(false);
  const { useQuery } = northboundAPIs.v1.graphql.reactTools;

  const allowedDeviceTypes = useMemo(
    () => [
      DeviceTypeParam.PRINTER,
      DeviceTypeParam.PC,
      DeviceTypeParam.NOTEBOOK,
      DeviceTypeParam.CHROMEBOOK,
      DeviceTypeParam.DESKTOP
    ],
    []
  );

  const { loading, error, data, refetch } = useQuery(GET_DEVICES, {
    variables: {
      limit: 200,
      deviceType: allowedDeviceTypes
    }
  });

  const { isRefetching, handleRefetch } = useRefetch({
    refetch,
    error,
    operation
  });

  // This filter is temporary, pending a fix in the api-shell-aggregation.
  // The query is not applying the filter correctly, resulting in the return of all devices.
  const filteredDevices = useMemo(() => {
    return data?.devices?.items?.filter((item: Device) =>
      allowedDeviceTypes.includes(item.type as DeviceTypeParam)
    );
  }, [data, allowedDeviceTypes]);

  const navigation = northboundAPIs.v1.navigation;
  const country = northboundAPIs.v1.localization.country;
  const language = northboundAPIs.v1.localization.language;

  const showOriginalFeedbackExperience =
    originalFeedbackExperience && !isLoading;

  useEffect(() => {
    if (!loading && data) {
      const DeviceCt = getAnalyticsData(filteredDevices);
      publishEvent(DevicesScreenDisplayed(DeviceCt));
    }
  }, [loading, data, filteredDevices]);

  const getAnalyticsData = (devices: Device[]) => {
    const deviceTypeMapping: { [key: string]: DeviceType } = {
      PrinterDevice: DeviceType.PRINTER_DEVICE,
      PCDevice: DeviceType.PC_DEVICE
    };

    const totalPrinter = devices?.filter(
      (item: Device) =>
        deviceTypeMapping[item.__typename] === DeviceType.PRINTER_DEVICE
    ).length;

    const totalPCDevice = devices?.filter(
      (item: Device) =>
        deviceTypeMapping[item.__typename] === DeviceType.PC_DEVICE
    ).length;
    const totalDevice = totalPrinter + totalPCDevice;

    return `totalDeviceCt=${totalDevice}&totalPrinterCt=${totalPrinter}&totalPcCt=${totalPCDevice}`;
  };

  const renderDevices = useMemo(() => {
    const refreshButton = () => {
      return (
        <RefreshSection>
          <div>
            <Button
              data-testid="refresh-button"
              appearance="ghost"
              leadingIcon={<IconReload />}
              loading={refetchLoading}
              onClick={() => {
                setRefetchLoading(true);
                publishEvent(RefreshDevicesButtonClicked);
                refetch().finally(() => {
                  setRefetchLoading(false);
                });
              }}
            >
              {`${translate('common.refresh', 'Refresh')}`}
            </Button>
          </div>
        </RefreshSection>
      );
    };

    const noDevices = () => {
      return (
        <NoDevicesSection
          data-testid="no-devices-section"
          background="filled"
          border="outlined"
        >
          <NoDevicesSectionContent>
            <IconGhost size={40} />
            <NoDevices>{`${translate('devices.noDevicesYet', 'No devices yet!')}`}</NoDevices>
          </NoDevicesSectionContent>
        </NoDevicesSection>
      );
    };

    const deviceList = () => {
      return (
        <DevicesList data-testid="devices-list">
          {filteredDevices?.map((item: Device) => (
            <DeviceCard
              key={item.deviceId}
              item={item}
              onClick={() =>
                navigation.push(
                  `/${country}/${language}/devices/${item.deviceId}`
                )
              }
            />
          ))}
        </DevicesList>
      );
    };

    const noDevicesFound = filteredDevices?.length <= 0;

    return (
      <DevicesContainer>
        {noDevicesFound ? noDevices() : deviceList()}
        {refreshButton()}
      </DevicesContainer>
    );
  }, [
    country,
    filteredDevices,
    language,
    navigation,
    refetch,
    refetchLoading,
    translate
  ]);

  return (
    <>
      <Header data-testid="header-devices">
        <SectionHeader
          title={translate('menu.devices', 'Devices')}
          subtitle={translate(
            'devices.viewOrManageCurrentDevices',
            'View or manage your current HP devices'
          )}
        />
      </Header>
      <ContainerContent>
        {loading && (
          <Section
            background="filled"
            border="outlined"
          >
            <Loading />
          </Section>
        )}
        {validateObjectProperties(error) && (
          <ErrorBoundary
            analyticsEvent={DevicesError(error?.message)}
            showOutline={true}
            loading={isRefetching}
            onRetry={handleRefetch}
            errorInfo={{
              error,
              context: 'Device',
              operation: operation
            }}
          />
        )}
        {!loading && !error && renderDevices}
        <DoNotSeeDevice
          seeDevicesPurchasedOnlyForUS={seeDevicesPurchasedOnlyForUS}
          isLoading={isLoading}
          showControlHubLabel={showControlHubLabel}
        />
      </ContainerContent>
      {showOriginalFeedbackExperience && (
        <LookingForSomethingElse
          analytics={{
            screenName: ScreenName.DEVICES,
            screenPath: screenPath
          }}
        />
      )}
    </>
  );
};
export default Devices;
